import "../styles/tails-config.css";
import "../styles/styles.css";
import PageWithLayoutType from "@/lib/pageWithLayouts";
import { SessionProvider, useSession } from "next-auth/react";
import type { AppProps } from "next/app";
import Head from "next/head";
import { ReactElement, useEffect } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import utils from "@/shared/utils";
import Lottie from "lottie-react";
import loadingAnime from "../public/loading.json";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import ApiHelper from "@/shared/ApiHelper";
import Script from "next/script";
import { useRouter } from "next/router";
import analytics, { GA_ID, rotaDoFunil } from "@/lib/analytics";
import CookieConsent from "@/components/CookieConsent";

type AppLayoutProps = AppProps & {
  Component: PageWithLayoutType;
  pageProps: any;
};

export default function App({ Component, pageProps }: AppLayoutProps) {
  utils.setCookie("timezone", utils.obterTimezone());
  utils.setCookie(
    "horario",
    new Date().getHours().toString() + ":" + new Date().getMinutes().toString()
  );

  // Não há service worker no projeto (public/service-worker.js não existe):
  // registrar fazia toda página pedir um arquivo inexistente (404).
  // Se um dia houver, registre aqui novamente.

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={String(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY)}
      useRecaptchaNet={true}
    >
      {/* <Auth> */}
      {/* viewport e título padrão ficam aqui (no _document o Next não permite);
          as páginas podem trocar o título com o próprio <Head> */}
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" key="viewport" />
        <title>Agência Mix - Gestão de Venda e Controle de Acesso. Venda de ingressos para Shows, Feiras, Festas, Rodeios e Eventos em Geral</title>
      </Head>
      {/* Google Analytics 4 (gtag.js); os eventos ficam em lib/analytics.ts.
          Só grava cookies após o consentimento (padrão negado no _document) */}
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`}
        strategy="afterInteractive"
      />
      <Script id="ga4" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${GA_ID}');
        `}
      </Script>
      <SessionProvider session={pageProps.session}>
        <SessaoApi />
        <AnalyticsGlobal />
        <RecaptchaApi />
        <Component {...pageProps} />
        <CookieConsent />
        <ToastContainer />
      </SessionProvider>
      {/* </Auth> */}
    </GoogleReCaptchaProvider>
  );
}

// Disponibiliza o reCAPTCHA v3 (transparente) para o ApiHelper
function RecaptchaApi() {
  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    ApiHelper.executarRecaptcha = executeRecaptcha ?? null;
  }, [executeRecaptcha]);

  return null;
}

// Repassa o token de sessão para o ApiHelper (só no navegador)
function SessaoApi() {
  const { data: session } = useSession();
  const token = session?.user?.session_token ?? "";

  useEffect(() => {
    ApiHelper.sessionToken = token;
  }, [token]);

  return null;
}

// Usuário logado no GA e detecção de carrinho abandonado
function AnalyticsGlobal() {
  const { data: session, status } = useSession();
  const router = useRouter();
  const userId = session?.user?.sub;

  useEffect(() => {
    if (status === "loading") return;
    analytics.definirUsuario(userId);
  }, [userId, status]);

  useEffect(() => {
    // saiu do checkout para fora do funil de compra (navegação interna)
    const aoTrocarRota = (url: string) => {
      if (window.location.pathname.startsWith("/checkout") && !rotaDoFunil(url)) {
        analytics.carrinhoAbandonado("saiu_do_checkout");
      }
    };
    // fechou a aba / foi para outro site com itens no carrinho
    const aoSairDoSite = () => analytics.carrinhoAbandonado("saiu_do_site");

    router.events.on("routeChangeStart", aoTrocarRota);
    window.addEventListener("pagehide", aoSairDoSite);
    return () => {
      router.events.off("routeChangeStart", aoTrocarRota);
      window.removeEventListener("pagehide", aoSairDoSite);
    };
  }, [router]);

  return null;
}

function Auth({ auth, children }: any) {
  // if `{ required: true }` is supplied, `status` can only be "loading" or "authenticated"
  const { status } = useSession({ required: true });
  if (status === "loading") {
    return (
      <div className="w-full h-screen flex justify-center items-center">
        <Lottie
          className="w-52 h-52 xl:col-span-2 m-auto"
          animationData={loadingAnime}
          autoPlay={true}
          loop={true}
        />
      </div>
    );
  }

  return children;
}
